import { useMemo } from 'react';

import { Table, TableLoadingRow, useIsDeviceSize } from '@foradevelopment/fora-ui';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';
import { PayrollReportHistoryRowViewModel } from '@fora-monorepo/employer-my-pages-common/types';

import { ReportHistoryTableSortId } from '../../../common/types';
import { useAppSelector } from '../../../redux/hooks';
import { ReportHistorySortIdKeys, ReportHistorySortState } from './ReportHistoryListSortState';
import { ReportHistoryTableRow } from './ReportHistoryTableRow';
import { TypedTFunction, useTypedTranslation } from '../../../i18n/i18n';

type ReportHistoryTableProps = {
  reportHistory: PayrollReportHistoryRowViewModel[];
  reportHistoryListIndices: number[];
  sortTable?: (sortId: ReportHistorySortIdKeys) => void;
  sortState: ReportHistorySortState;
};

export const ReportHistoryTable = ({
  reportHistory,
  reportHistoryListIndices,
  sortTable,
  sortState,
}: ReportHistoryTableProps) => {
  const { t } = useTypedTranslation();
  const { isMobile, isTablet } = useIsDeviceSize();

  const isLoadingReportHistory = useAppSelector(state => state.reportHistory.loadingReportHistory);

  const tableColumns = useMemo(() => {
    return getTableHeadCells(isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop', t);
  }, [isMobile, isTablet, t]);

  return (
    <Table withBorderedRows={true} withExpandableRows={true}>
      <Table.Head>
        <Table.HeadRow>
          {tableColumns.map((cell, index) => {
            return (
              <Table.HeadCell
                key={`${cell.id}-${index}`}
                sortId={cell.id}
                onToggle={
                  cell.id !== ReportHistoryTableSortId.Receipt && cell.id !== ReportHistoryTableSortId.Documents
                    ? (sortTable as (id: string) => void)
                    : undefined
                }
                state={sortState[cell.id]}
              >
                {cell.name}
              </Table.HeadCell>
            );
          })}
        </Table.HeadRow>
      </Table.Head>
      <Table.Body>
        {isLoadingReportHistory === ThunkRequestStatus.Pending ? (
          <TableLoadingRow />
        ) : (
          reportHistoryListIndices.map(reportHistoryListIndex => (
            <ReportHistoryTableRow
              key={`employment-table-row-${reportHistoryListIndex}`}
              reportHistory={reportHistory[reportHistoryListIndex]}
              reportIndex={reportHistoryListIndex}
            />
          ))
        )}
      </Table.Body>
    </Table>
  );
};

type TableHeadCell = {
  name: string;
  id: ReportHistoryTableSortId;
}[];

const getTableHeadCells = (variant: 'mobile' | 'tablet' | 'desktop', t: TypedTFunction): TableHeadCell => {
  switch (variant) {
    case 'mobile':
      return [
        { name: t('table.reportLog.columnDate-1'), id: ReportHistoryTableSortId.Date },
        { name: t('table.reportLog.columnCompany-1'), id: ReportHistoryTableSortId.Organization },
      ];
    case 'tablet':
      return [
        { name: t('table.reportLog.columnDate-1'), id: ReportHistoryTableSortId.Date },
        { name: t('table.reportLog.columnStatus-1'), id: ReportHistoryTableSortId.Status },
        { name: t('table.reportLog.columnCompany-1'), id: ReportHistoryTableSortId.Organization },
        { name: t('table.reportLog.columnReporter-1'), id: ReportHistoryTableSortId.Administrator },
        { name: t('table.reportLog.columnDocuments-1'), id: ReportHistoryTableSortId.Documents },
      ];
    case 'desktop':
    default:
      return [
        { name: t('table.reportLog.columnDate-1'), id: ReportHistoryTableSortId.Date },
        { name: t('table.reportLog.columnStatus-1'), id: ReportHistoryTableSortId.Status },
        { name: t('table.reportLog.columnCompany-1'), id: ReportHistoryTableSortId.Organization },
        { name: t('table.reportLog.columnReporter-1'), id: ReportHistoryTableSortId.Administrator },
        { name: t('table.reportLog.columnReceipt-1'), id: ReportHistoryTableSortId.Receipt },
        { name: t('table.reportLog.columnDocuments-1'), id: ReportHistoryTableSortId.Documents },
      ];
  }
};
